import { fetchWrapper, API_URL } from './fetchWrapper';

function buildUrl(path: string, params?: Record<string, unknown>) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!params) return `${API_URL}${normalizedPath}`;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null) searchParams.set(key, String(value));
  }
  const qs = searchParams.toString();
  return qs ? `${API_URL}${normalizedPath}?${qs}` : `${API_URL}${normalizedPath}`;
}

export type ListResponse<T> = { items: T[]; total: number };

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    fetchWrapper(buildUrl(path, params), { method: 'GET' }, `GET ${path}`) as Promise<{
      data: T;
    }>,

  getPaginated: async <T>(
    path: string,
    params: { page: number; size?: number } & Record<string, unknown>,
  ): Promise<ListResponse<T>> => {
    const { size = 10, ...rest } = params;
    const { data } = await api.get<T[] | ListResponse<T>>(path, { ...rest, size });
    if (Array.isArray(data)) return { items: data, total: data.length };
    return { items: data?.items ?? [], total: data?.total ?? 0 };
  },

  post: <T>(path: string, body?: unknown) =>
    fetchWrapper(
      buildUrl(path),
      {
        method: 'POST',
        body: body != null ? JSON.stringify(body) : undefined,
        headers: { 'Content-Type': 'application/json' },
      },
      `POST ${path}`,
    ) as Promise<{ data: T }>,

  patch: <T>(path: string, body?: unknown) =>
    fetchWrapper(
      buildUrl(path),
      {
        method: 'PATCH',
        body: body != null ? JSON.stringify(body) : undefined,
        headers: { 'Content-Type': 'application/json' },
      },
      `PATCH ${path}`,
    ) as Promise<{ data: T }>,

  put: <T>(path: string, body?: unknown) =>
    fetchWrapper(
      buildUrl(path),
      {
        method: 'PUT',
        body: body != null ? JSON.stringify(body) : undefined,
        headers: { 'Content-Type': 'application/json' },
      },
      `PUT ${path}`,
    ) as Promise<{ data: T }>,

  delete: <T>(path: string) =>
    fetchWrapper(buildUrl(path), { method: 'DELETE' }, `DELETE ${path}`) as Promise<{ data: T }>,
};
